import { Button, Modal, message, notification, Flex } from 'antd'
import { FileExcelOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAsync } from '../../hooks'
import useInterval from '../../hooks/useInterval'
import useExportExcel from '../../hooks/useExportExcel'
import { useIsAdmin, useCurrentUser } from '../../redux/slices/session'
import {
  useReports,
  useIsAcknowledged,
  useFetchReports,
  markReportsSigned,
} from '../../redux/slices/reports'
import { toPath } from '../../routes'
import { signReports, getBatchSignatureStatus } from '../../api/reports'
import { ListError, PageHeader } from '../Common'
import CyclesFilter from '../Common/CyclesFilter'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'

import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import api from '../../api'
import Reports from './Reports'
import BatchDownloadButton from './BatchDownloadButton'

const BATCH_STORAGE_KEY = 'activePdfBatch'
const POLL_INTERVAL = 2000

const DEFAULT_FILTERS = {
  signed: FIELDS_OPTIONS.signed[0].id,
  query: '',
}

function readBatchFromStorage() {
  try {
    const stored = localStorage.getItem(BATCH_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export default function PracticeReports() {
  const dispatch = useDispatch()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const isAdmin = useIsAdmin()
  const [{ error, clearError }, { practice, pending, practiceId }] =
    usePracticeCycles()
  const [officeLocations, setOfficeLocations] = useState([])
  const user = useCurrentUser()
  const isProvider = user?.position === 'provider'
  const [hasInteracted, setHasInteracted] = useState(false)
  const { conditions, setConditions, handleFilterChange, handlePageChange } =
    useFilters({
      ...DEFAULT_FILTERS,
      practiceId,
      medicalProviderId: isProvider && !hasInteracted ? user?.id : undefined,
    })
  const isAcknowledged = useIsAcknowledged(conditions.cycleId)
  const reportFilters = useMemo(
    () => ({
      q: conditions.query,
      signed: conditions.signed,
      medicalProviderId: conditions.medicalProviderId,
      deviceTypeId: conditions.deviceTypeId,
      officeLocationId: conditions.officeLocationId,
    }),
    [conditions],
  )

  const { cycleId, page, perPage } = conditions

  const { pending: reportPending, execute: fetchReports } = useAsync(
    useFetchReports(practiceId, cycleId, page, reportFilters, perPage),
    false,
  )

  const reports = useReports(cycleId)

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/cycles/${cycleId}/reports.xlsx`,
  )

  const [activeBatchId, setActiveBatchId] = useState(
    () => readBatchFromStorage()?.batchId ?? null,
  )

  const sign = async () => {
    try {
      const res = await signReports(practiceId, selectedRowKeys)
      setIsModalVisible(false)

      const { length } = res.reports
      message.success(`${length} report${length !== 1 ? 's' : ''} signed.`)

      dispatch(markReportsSigned(res.reports))

      const reportIds = res.reports.map(r => r.remoteCheckId)
      localStorage.setItem(
        BATCH_STORAGE_KEY,
        JSON.stringify({ batchId: res.batchId, reportIds }),
      )
      setActiveBatchId(res.batchId)
      setSelectedRowKeys([])
    } catch (e) {
      message.error('Failed to sign reports, please try again.')
    }
  }

  const pollBatchStatus = async () => {
    if (!activeBatchId) return
    try {
      const data = await getBatchSignatureStatus(activeBatchId)

      if (data.error) {
        setActiveBatchId(null)
        localStorage.removeItem(BATCH_STORAGE_KEY)
        message.error(`Error generating PDFs: ${data.error}`)
        return
      }

      if (data.finishedAt) {
        setActiveBatchId(null)
        localStorage.removeItem(BATCH_STORAGE_KEY)
        notification.success({
          message: 'Reports ready',
          description: 'PDFs have been generated and are ready to download.',
        })
        setConditions({ ...conditions, page: 1 })
      }
    } catch {
      // silent fail — keep polling on next tick
    }
  }

  useInterval(pollBatchStatus, activeBatchId ? POLL_INTERVAL : null)

  useEffect(() => {
    if (practiceId) {
      api.practices.getPracticeOfficeLocations(practiceId).then(data => {
        setOfficeLocations(data)
      })
    }
  }, [practiceId])

  if (cycleId && !pending) {
    return (
      <div>
        <PageHeader
          title="Remote Reports"
          subTitle={isAdmin && practice.name}
          extra={[
            selectedRowKeys.length > 0 && (
              <Button key="sign" onClick={() => setIsModalVisible(true)}>
                {`Sign ${selectedRowKeys.length} report${
                  selectedRowKeys.length !== 1 ? 's' : ''
                }`}
              </Button>
            ),
            isAdmin && (
              <Link
                key="upload"
                to={toPath.cycles.cycle.reports.upload({ id: cycleId })}
              >
                <Button>Upload reports</Button>
              </Link>
            ),
            (isAcknowledged || isAdmin) && (
              <BatchDownloadButton
                key="download"
                cycleId={cycleId}
                pdfPending={!!activeBatchId}
              />
            ),
          ]}
        />
        <Flex gap={10} vertical>
          <CyclesFilter
            practiceId={practiceId}
            onSubmit={handleFilterChange}
            currentCycleId={cycleId}
          />
          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={conditions}
            placeholders={{ query: 'Search by name or device serial number' }}
            onSubmit={({
              query,
              signed,
              medicalProviderId,
              deviceTypeId,
              officeLocationId,
            }) => {
              setHasInteracted(true)
              setConditions({
                cycleId,
                query,
                signed,
                medicalProviderId,
                deviceTypeId,
                officeLocationId,
                page: 1,
              })
            }}
            showDeviceType
            showMedicalProvider
            showSigningStatus
            showSearch
            showOfficeLocation={practiceId && officeLocations.length > 0}
          />
        </Flex>
        <Modal
          title="Sign reports"
          open={isModalVisible}
          onOk={sign}
          onCancel={() => setIsModalVisible(false)}
          okText="Sign"
          cancelText="Cancel"
        >
          <p>{`You are about to sign ${
            selectedRowKeys.length !== 1
              ? `a batch of ${selectedRowKeys.length} reports`
              : '1 report'
          }.`}</p>
        </Modal>
        <Reports
          practiceId={practiceId}
          cycleId={cycleId}
          page={page}
          filters={reportFilters}
          onPageChange={handlePageChange}
          selected={selectedRowKeys}
          onSelect={setSelectedRowKeys}
          fetchReports={fetchReports}
          pendingFetch={reportPending}
        />
        {Boolean(reports.length > 0) && (
          <div>
            <Button
              loading={exportingExcel}
              onClick={exportExcel}
              icon={<FileExcelOutlined />}
            >
              Export this list to Excel
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
